import "./App.css";

import React from "react";
import {
  Button,
  Card,
  CardContent,
  TextField,
  Container,
  Grid,
  Typography,
  Box,
  Alert,
} from "@mui/material";

function RegistrationForm({
  formData,
  registrationMessage,
  handleChange,
  handleTeamMemberChange,
  handleAddTeamMember,
  handleDeleteTeamMember,
  handleSubmit,
  redirecting,
  teamNameError,
}) {
  if (redirecting) {
    return (
      <Box className="App" sx={{ textAlign: "center", padding: 4 }}>
        <Typography variant="h5" color="success.main">
          {registrationMessage}
        </Typography>
        <Typography variant="h6">Redirecting to the login page...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#f4f4f4",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        py: { xs: 2, sm: 3 }
      }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
        <Card elevation={5} sx={{ 
          borderRadius: "16px", 
          padding: { xs: 2, sm: 3, md: 4 }, 
          position: "relative", 
          zIndex: 1,
          maxWidth: "100%"
        }}>
          <CardContent>
            <Box sx={{ marginBottom: 2 }}>
              <Button
                variant="outlined"
                onClick={() => window.location.href = "/login"}
                sx={{
                  borderColor: "#2c3e50",
                  color: "#2c3e50",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  padding: { xs: "6px 12px", sm: "8px 16px" },
                  "&:hover": { 
                    backgroundColor: "#f5f5f5",
                    borderColor: "#1f2a36",
                  },
                }}
              >
                ← Back to Login
              </Button>
            </Box>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{ 
                color: "#2c3e50", 
                fontWeight: "bold",
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" }
              }}
            >
              Florida Step Team Registration
            </Typography>
            {registrationMessage && (
              <Alert severity="success" sx={{ marginBottom: 2 }}>
                {registrationMessage}
              </Alert>
            )}
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                    Select Crop Type:
                  </Typography>
                  <Box sx={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    gap: { xs: 1, sm: 2 },
                    flexWrap: "wrap"
                  }}>
                    {["corn", "cotton"].map((crop) => (
                      <Button
                        key={crop}
                        variant={formData.cropType === crop ? "contained" : "outlined"}
                        onClick={() => handleChange({ target: { name: "cropType", value: crop } })}
                        sx={{
                          fontSize: { xs: "1rem", sm: "1.2rem" },
                          padding: { xs: "8px 16px", sm: "12px 24px" },
                          flex: { xs: "1 1 120px", sm: "0 1 auto" },
                          minWidth: { xs: "120px", sm: "auto" },
                          backgroundColor: formData.cropType === crop ? "#2c3e50" : "transparent",
                          color: formData.cropType === crop ? "white" : "#2c3e50",
                          borderColor: "#2c3e50",
                          "&:hover": {
                            backgroundColor: "#2c3e50",
                            color: "white",
                          },
                        }}
                      >
                        {crop.charAt(0).toUpperCase() + crop.slice(1)}
                      </Button>
                    ))}
                  </Box>
                </Grid>

                {/* Team Name field with validation */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Team Name"
                    name="teamName"
                    value={formData.teamName}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    error={!!teamNameError}
                    helperText={teamNameError || "Only letters, numbers, spaces, hyphens, and underscores allowed"}
                    sx={{ marginBottom: 2 }}
                  />
                </Grid>

                {["password", "captainFirstName", "captainLastName", "address1","address2", "city", "state", "zipCode", "country", "email", "phone"].map((field) => (
                  
                  <Grid item xs={12} sm={field.includes("FirstName") || field.includes("LastName") || field.includes("city") || field.includes("state") || field.includes("zipCode") ? 6 : 12} key={field}>
                    <TextField
                      fullWidth
                      label={field.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase())}

                      name={field}
                      type={field === "password" ? "password" : "text"}
                      value={formData[field]}
                      onChange={handleChange}
                      required = {field !== "address2"}
                      variant="outlined"
                      sx={{ marginBottom: 2 }}
                    />
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                    Team Members:
                  </Typography>
                  {formData.teamMembers.map((member, index) => (
                    <Box key={index} sx={{ display: "flex", flexDirection: "column", gap: 1, marginBottom: 2 }}>
                      <TextField
                        fullWidth
                        label={`Team Member ${index + 1} Name`}
                        value={member.name}
                        onChange={(e) => handleTeamMemberChange(index, "name", e.target.value)}
                      />
                      <TextField
                        fullWidth
                        label={`Team Member ${index + 1} Email`}
                        value={member.email}
                        onChange={(e) => handleTeamMemberChange(index, "email", e.target.value)}
                      />
                      <Button
                        type="button"
                        onClick={() => handleDeleteTeamMember(index)}
                        variant="outlined"
                        color="error"
                        sx={{ width: { xs: "100%", sm: "50%" } }}
                      >
                        Delete Team Member
                      </Button>
                    </Box>
                  ))}
                  <Button
                    type="button"
                    onClick={handleAddTeamMember}
                    variant="outlined"
                    color="secondary"
                    sx={{ width: { xs: "100%", sm: "50%" } }}
                  >
                    Add Team Member
                  </Button>
                </Grid>
              </Grid>
              <Box sx={{ marginTop: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: "#2c3e50",
                    color: "white",
                    fontSize: "1.2rem",
                    padding: "12px 0",
                    "&:hover": { backgroundColor: "#1f2a36" },
                  }}
                >
                  Register
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default RegistrationForm;
